import { ENGLISH_WORDLIST, isInWordlist } from "./wordlist";
import { classifyMnemonic } from "./validate";
import { closestWords } from "./editDistance";
import type { MnemonicCandidate } from "./types";

export interface GeneratorOptions {
  /**
   * Also yield candidates that fail the structural/checksum check (with
   * their validity set accordingly). Off by default. A caller that needs to
   * count work done or regain control periodically (btc-adapter's
   * runRecovery: progress + cancellation) turns this on and filters itself —
   * otherwise a long stretch of invalid combinations never yields at all.
   */
  includeInvalid?: boolean;
}

/**
 * `template` uses `null` for blank/unknown positions. Yields every
 * combination, cartesian-product style, checksum-filtered. Caller is
 * responsible for confirming feasibility via estimateMissingWordsSpace
 * before calling this on more than ~2 blanks.
 */
export function* generateMissingWordCandidates(
  template: (string | null)[],
  options: GeneratorOptions = {}
): Generator<MnemonicCandidate> {
  const blankIndices = template
    .map((w, i) => (w === null ? i : -1))
    .filter((i) => i !== -1);

  if (blankIndices.length === 0) {
    const mnemonic = (template as string[]).join(" ");
    yield { mnemonic, validity: classifyMnemonic(mnemonic) };
    return;
  }

  // Fill a private copy: cartesianFill mutates its array in place, and a
  // consumer that stops early (cancellation, a verified match) never lets
  // it restore the blanks — the caller's template must not be left altered.
  yield* cartesianFill([...template], blankIndices, 0, options.includeInvalid ?? false);
}

function* cartesianFill(
  template: (string | null)[],
  blankIndices: number[],
  depth: number,
  includeInvalid: boolean
): Generator<MnemonicCandidate> {
  if (depth === blankIndices.length) {
    const mnemonic = (template as string[]).join(" ");
    const validity = classifyMnemonic(mnemonic);
    if (includeInvalid || validity === "checksum-valid-unverified") {
      yield { mnemonic, validity };
    }
    return;
  }
  const idx = blankIndices[depth];
  for (const word of ENGLISH_WORDLIST) {
    template[idx] = word;
    yield* cartesianFill(template, blankIndices, depth + 1, includeInvalid);
  }
}

/**
 * For a word the user isn't sure of. Returns edit-distance suggestions
 * (fast, shown to the user immediately) plus, if requested, the full
 * checksum-filtered brute force across all 2048 words (also fast — this
 * is a single blank slot).
 */
export function suggestReplacementsForWord(word: string, limit = 8) {
  return closestWords(word.toLowerCase(), ENGLISH_WORDLIST, limit);
}

export function* generateIncorrectWordCandidates(
  words: string[],
  suspectIndex: number,
  options: GeneratorOptions = {}
): Generator<MnemonicCandidate> {
  const template = [...words] as (string | null)[];
  template[suspectIndex] = null;
  yield* generateMissingWordCandidates(template, options);
}

/**
 * Tries every permutation of the given words. Only call this for a small
 * number of positions (<= 8 is instant, <= 10 is seconds, beyond that use
 * estimateWordOrderSpace first and get user confirmation).
 */
export function* generateWordOrderCandidates(
  words: string[],
  maxPositions = 10,
  options: GeneratorOptions = {}
): Generator<MnemonicCandidate> {
  if (words.length > maxPositions) {
    throw new Error(
      `Refusing to permute ${words.length} words automatically (${words.length}! is too large). ` +
        `Narrow down which positions are likely swapped first.`
    );
  }
  for (const perm of permutations(words)) {
    const mnemonic = perm.join(" ");
    const validity = classifyMnemonic(mnemonic);
    if (options.includeInvalid || validity === "checksum-valid-unverified") {
      yield { mnemonic, validity };
    }
  }
}

function* permutations<T>(arr: T[]): Generator<T[]> {
  if (arr.length <= 1) {
    yield arr;
    return;
  }
  for (let i = 0; i < arr.length; i++) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    for (const perm of permutations(rest)) {
      yield [arr[i], ...perm];
    }
  }
}

export function validateAllWordsKnown(words: string[]): { index: number; word: string }[] {
  return words
    .map((w, i) => ({ index: i, word: w }))
    .filter(({ word }) => word !== "" && !isInWordlist(word));
}
